import { Injectable } from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import {
    addDays,
    endOfMonth,
    endOfToday,
    endOfWeek,
    isSameDay,
    startOfDay,
    startOfTomorrow,
    subDays,
} from '@core/date.facade';
import {
    toReservationValidUntilDefaultTime,
} from '@shared/functions/apply-reservation-default-times';
import { SharedLayerService } from '@modules/bcm/berths/berths-map/_shared/shared-layer.service';
import { DateRangeType } from '@modules/bcm/berths/berths-map/berth-map-action-bar/berth-map-action-bar.component';
import { HttpParams } from '@angular/common/http';
import { BerthsFacadeOld } from '@bcmServices/berths/berths-facade-old.service';
import { ElectricMeterCabinetsFacade } from '@bcmServices/electric-meters/electric-meter-cabinets-facade';
import {
    BERTH_MAP_NOW_WINDOW_MINUTES,
    floorDateToBerthMapGrid,
    getBerthMapElectricMeterCabinetParams,
} from '@bcmServices/berths/berths-map-refresh.utils';
import { U2bNumericValidators } from '@shared/validators/numeric';
import { toSqlDateTime } from '@core/functions/to-date-string';
import { parseInt } from 'lodash';
import { Boat } from '@shared/models/boat';
import { BehaviorSubject, Subject, take } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';
import { deepEqual } from '@shared/functions/deep-equal';
import { isValidDate } from '@core/functions/is-valid-date';
import { BcmSettingsFacade } from '@bcmServices/settings/bcm-settings-facade';
import { BcmSettingsSectionName, DefaultBerthReservationTimeUnit } from '@shared/models/bcm-settings';

export type LastFilter = {
    timestamp: number,
    start: Date,
    end: Date,
    length: number,
    width: number,
    maxDraft: number,
    boat?: Boat,
};

export interface DateRange {
    start: Date | null;
    end: Date | null;
}

/** Jetzt = Momentaufnahme, Zeitraum = Von–Bis für eine Anfrage. */
export type BerthMapViewMode = 'now' | 'range';

/** Gesprochene Anfrage am Telefon, ein Klick füllt Von und Bis. */
export type BerthMapSituation = 'today' | 'tonight' | 'tomorrow';

const EVENING_ARRIVAL_HOUR = 18;

// Boat context of the current map search: either the picked boat, or — when the
// user only typed dimensions — those dimensions. Never both.
export interface BerthMapBookingPrefill {
    boat?: Boat;
    boatProperties?: { length: number, width: number, depth: number };
}

function currentDayRange(): DateRange {
    return {start: startOfDay(new Date()), end: endOfToday()};
}

/** Gesetzte Stunde und Minute. Leer, null oder ungültig zählt als nicht hinterlegt. */
function configuredTimeOfDay(
    hours: number | string | null | undefined,
    minutes: number | string | null | undefined,
): {hours: number; minutes: number} | null {
    if (hours === null || hours === undefined || hours === ''
        || minutes === null || minutes === undefined || minutes === '') {
        return null;
    }
    const parsedHours = Number(hours);
    const parsedMinutes = Number(minutes);
    if (Number.isNaN(parsedHours) || Number.isNaN(parsedMinutes)) {
        return null;
    }
    return {hours: parsedHours, minutes: parsedMinutes};
}

function positiveDimension(value: unknown): number {
    const parsed = Number(value);
    return isFinite(parsed) && parsed > 0 ? parsed : 0;
}

@Injectable({providedIn: 'root'})
export class BerthsMapFilterService {

    private readonly updateBerthViewRequest$ = new Subject<{ keepBerthFiltersOpen: boolean; silent: boolean }>();

    private centralDateRangeSubject = new BehaviorSubject<DateRange>(currentDayRange());

    public centralDateRange = this.centralDateRangeSubject
        .pipe(
            debounceTime(50),
            filter(({start, end}) => isValidDate(start) && (isValidDate(end) || !end)),
            distinctUntilChanged((a, b) => deepEqual(a, b)),
        );

    private activeFavouriteIdSubject = new BehaviorSubject<string | null>(null);

    public activeFavouriteId$ = this.activeFavouriteIdSubject.asObservable();

    currentDateSelectType = DateRangeType.Today;

    private liveModeSubject = new BehaviorSubject<boolean>(false);

    readonly liveMode$ = this.liveModeSubject.asObservable();

    private viewModeSubject = new BehaviorSubject<BerthMapViewMode>('range');

    readonly viewMode$ = this.viewModeSubject.asObservable();

    /** Letztes Von–Bis, damit „Zeitraum“ nach einer Momentaufnahme wiederhergestellt werden kann. */
    private rememberedRange: DateRange | null = null;

    private rememberedSituation: BerthMapSituation | null = 'today';

    private situationSubject = new BehaviorSubject<BerthMapSituation | null>('today');

    readonly situation$ = this.situationSubject.asObservable();

    /** Programm setzt die Spanne passend zur Situation, ohne das Preset zu löschen. */
    private keepSituation = false;

    private suppressLiveModeExit = false;

    private skipNextCentralViewUpdate = false;

    defaultBerthReservationTimeUnit: DefaultBerthReservationTimeUnit;

    private _advancedBerthFiltersOpen: boolean;

    public get advancedBerthFiltersOpen(): boolean {
        return this._advancedBerthFiltersOpen;
    }

    private _berthFormGroup = new FormGroup<{
        length: AbstractControl<number>,
        width: AbstractControl<number>,
        maxDraft: AbstractControl<number>,
        boatType: AbstractControl<string>,
        boat: AbstractControl<Boat>,
    }>({
        length: new FormControl(null,
            [
                U2bNumericValidators.numberFormat(),
                U2bNumericValidators.numberMin(0, 'Keine negativen Werte erlaubt'),
            ]
        ),
        width: new FormControl(null,
            [
                U2bNumericValidators.numberFormat(),
                U2bNumericValidators.numberMin(0, 'Keine negativen Werte erlaubt'),
            ]
        ),
        maxDraft: new FormControl(null,
            [
                U2bNumericValidators.numberFormat(),
                U2bNumericValidators.numberMin(0, 'Keine negativen Werte erlaubt'),
            ]
        ),
        boatType: new FormControl(null),
        boat: new FormControl(null),
    });

    public get berthFormGroup(): FormGroup {
        return this._berthFormGroup;
    }

    private _lastBerthFilters: LastFilter[] = [];

    // Search-panel values of the newest non-silent load (the one `lastBerthFilters[0]` records).
    private _loadedSearch: unknown;

    get lastBerthFilters(): LastFilter[] {
        return this._lastBerthFilters;
    }

    get lastBerthFilter(): LastFilter {
        return this._lastBerthFilters.length ? this._lastBerthFilters[0] : {} as LastFilter;
    }

    constructor(private _berthFacade: BerthsFacadeOld,
                private _electricMeterCabinetsFacade: ElectricMeterCabinetsFacade,
                private _bcmSettingsFacade: BcmSettingsFacade) {
        this.updateBerthViewRequest$
            .pipe(debounceTime(100))
            .subscribe(({ keepBerthFiltersOpen, silent }) => this.loadBerthViewData(keepBerthFiltersOpen, silent));
        this.initSubscriptions();

        this._bcmSettingsFacade.settings$
            .pipe(
                filter(settings => !!settings),
            )
            .subscribe(settings => {
                const firstLoad = !this.defaultBerthReservationTimeUnit;
                this.defaultBerthReservationTimeUnit = settings[BcmSettingsSectionName.DefaultBerthReservationTimeUnit];
                if (!firstLoad) {
                    return;
                }
                this.keepSituation = true;
                this.setDateRange(this.viewingDayRange(new Date()), false);
                this.keepSituation = false;
                this.situationSubject.next('today');
                this.rememberedSituation = 'today';
            });

    }

    setActiveFavourite(id: string | null): void {
        this.activeFavouriteIdSubject.next(id);
    }

    setDateRange({start, end}: DateRange, setDefaultTime = true): void {

        start = isValidDate(start) ? start : null;
        end = isValidDate(end) ? end : null;

        if (!start) {
            return;
        }

        if (this.liveModeSubject.getValue() && !this.suppressLiveModeExit) {
            this.liveModeSubject.next(false);
        }

        if (setDefaultTime) {
            if (end) {
                ({start, end} = this.viewingDayRange(start, end));
            } else {
                start = this.viewingDayRange(start).start;
            }
        }

        this.centralDateRangeSubject.next({start, end: end});

        if (!this.isNowView()) {
            this.rememberedRange = {
                start: new Date(start),
                end: end ? new Date(end) : null,
            };
            if (!this.keepSituation) {
                this.situationSubject.next(null);
                this.rememberedSituation = null;
            }
        }
    }

    applySituation(situation: BerthMapSituation): void {
        this.beginRangeView();
        this.situationSubject.next(situation);
        this.rememberedSituation = situation;
        this.currentDateSelectType = situation === 'tomorrow'
            ? DateRangeType.Tomorrow
            : situation === 'today'
                ? DateRangeType.Today
                : DateRangeType.Custom;
        this.keepSituation = true;
        this.skipNextCentralViewUpdate = true;
        this.setDateRange(this.rangeForSituation(situation), false);
        this.keepSituation = false;
        this.updateBerthView();
    }

    /**
     * Ein Betrachtungstag beginnt zur hinterlegten Abreisezeit und endet um 23:59.
     * Ist keine Abreisezeit hinterlegt, gilt 00:00 bis 23:59.
     * Am heutigen Tag liegt der Beginn nicht vor der aktuellen Uhrzeit.
     */
    viewingDayRange(startDay: Date, endDay: Date = startDay): DateRange {
        const unit = this.defaultBerthReservationTimeUnit;
        const departure = configuredTimeOfDay(unit?.overnightGuestToHours, unit?.overnightGuestToMinutes);
        let start = startOfDay(startDay);
        start.setHours(departure?.hours ?? 0, departure?.minutes ?? 0, 0, 0);
        const now = new Date();
        if (isSameDay(start, now) && now.getTime() > start.getTime()) {
            start = new Date(now);
            start.setSeconds(0, 0);
        }
        const end = startOfDay(endDay);
        end.setHours(23, 59, 0, 0);
        return {start, end};
    }

    private rangeForSituation(situation: BerthMapSituation): DateRange {
        if (situation === 'today') {
            return this.viewingDayRange(new Date());
        }
        if (situation === 'tomorrow') {
            return this.viewingDayRange(startOfTomorrow());
        }
        const start = startOfDay(new Date());
        start.setHours(EVENING_ARRIVAL_HOUR, 0, 0, 0);
        const end = startOfTomorrow();
        const checkout = toReservationValidUntilDefaultTime(this.defaultBerthReservationTimeUnit);
        end.setHours(checkout.hours, checkout.minutes, 0, 0);
        return {start, end};
    }

    isLiveMode(): boolean {
        return this.liveModeSubject.getValue();
    }

    isNowView(): boolean {
        return this.viewModeSubject.getValue() === 'now';
    }

    private captureRange(): void {
        if (this.isNowView()) {
            return;
        }
        const current = this.getDateRange();
        if (!isValidDate(current.start)) {
            return;
        }
        this.rememberedRange = {
            start: new Date(current.start),
            end: current.end ? new Date(current.end) : null,
        };
        this.rememberedSituation = this.situationSubject.getValue();
    }

    getRememberedRange(): DateRange | null {
        if (!this.rememberedRange?.start) {
            return null;
        }
        return {
            start: new Date(this.rememberedRange.start),
            end: this.rememberedRange.end ? new Date(this.rememberedRange.end) : null,
        };
    }

    /** Momentaufnahme. Die zuletzt gewählte Spanne bleibt für „Zeitraum“ erhalten. */
    showNow(): void {
        if (this.isNowView()) {
            return;
        }
        this.captureRange();
        if (this.liveModeSubject.getValue()) {
            this.liveModeSubject.next(false);
        }
        this.viewModeSubject.next('now');
        this.currentDateSelectType = DateRangeType.Now;
        this.applyNowRange();
        this.updateBerthView();
    }

    /** Stellt die zuletzt betrachtete Spanne wieder her. */
    showRange(): void {
        if (!this.isNowView()) {
            return;
        }
        if (this.liveModeSubject.getValue()) {
            this.liveModeSubject.next(false);
        }
        this.viewModeSubject.next('range');
        this.currentDateSelectType = DateRangeType.Custom;
        const restored = this.getRememberedRange() ?? this.viewingDayRange(new Date());
        this.situationSubject.next(this.rememberedSituation);
        this.keepSituation = true;
        this.skipNextCentralViewUpdate = true;
        this.setDateRange(restored, false);
        this.keepSituation = false;
        this.updateBerthView();
    }

    /** Wechselt in den Zeitraum-Modus, ohne die Momentaufnahme als Spanne zu übernehmen. */
    beginRangeView(): void {
        if (this.liveModeSubject.getValue()) {
            this.liveModeSubject.next(false);
        }
        if (this.isNowView()) {
            this.viewModeSubject.next('range');
            this.currentDateSelectType = DateRangeType.Custom;
        }
    }

    consumeSkipCentralViewUpdate(): boolean {
        if (!this.skipNextCentralViewUpdate) {
            return false;
        }
        this.skipNextCentralViewUpdate = false;
        return true;
    }

    setLiveMode(enabled: boolean): void {
        if (enabled) {
            if (!this.isNowView()) {
                this.captureRange();
                this.viewModeSubject.next('now');
            }
            this.currentDateSelectType = DateRangeType.Now;
            if (!this.liveModeSubject.getValue()) {
                this.liveModeSubject.next(true);
            }
            this.applyNowRange();
            this.updateBerthView(false, true);
            return;
        }
        if (this.liveModeSubject.getValue()) {
            this.liveModeSubject.next(false);
        }
    }

    toggleLiveMode(): void {
        this.setLiveMode(!this.isLiveMode());
    }

    applyNowRange(): void {
        const start = floorDateToBerthMapGrid(new Date());
        const end = new Date(start.getTime() + BERTH_MAP_NOW_WINDOW_MINUTES * 60_000);
        this.suppressLiveModeExit = true;
        this.skipNextCentralViewUpdate = true;
        this.currentDateSelectType = this.isLiveMode() ? DateRangeType.Live : DateRangeType.Now;
        this.setDateRange({start, end}, false);
        this.suppressLiveModeExit = false;
    }

    selectNow(): void {
        this.showNow();
    }

    getDateRange(): DateRange {
        return this.centralDateRangeSubject.getValue();
    }

    // What the map search knows about the boat, for prefilling a booking dialog
    // opened from the map. Reads the live form (not `lastBerthFilters`), so it
    // also applies when the user picks a boat and books without hitting "search".
    getBookingPrefill(): BerthMapBookingPrefill {
        const {length, width, maxDraft, boat} = this._berthFormGroup.value;

        if (boat?.id) {
            return {boat};
        }

        // Empty inputs arrive as '' / null — only a complete set of positive
        // dimensions describes a boat; anything less must not become a 0.
        const dimensions = {length: positiveDimension(length), width: positiveDimension(width), depth: positiveDimension(maxDraft)};

        return Object.values(dimensions).every(value => value > 0)
            ? {boatProperties: dimensions}
            : {};
    }

    clearBoatSelection(): void {
        // Only the boat — length/width/maxDraft stay, they are the visible search
        // criteria and wiping them would silently reset the running map search.
        this._berthFormGroup.get('boat').setValue(null);

        // The legacy booking dialog and berth-reservation read the boat from the
        // newest history entry instead of the form (WorkFlowService.newBerthReservation),
        // so it has to be dropped there too — otherwise the removed boat keeps
        // prefilling every booking until the next search overwrites the entry.
        this.clearLatestBerthFilterSearchCriteria(false);
    }

    /** Whether the map search panel still holds boat or dimension filters. */
    hasActiveSearchCriteria(): boolean {
        const {length, width, maxDraft, boatType, boat} = this._berthFormGroup.value;

        return !!(boat?.id || boatType
            || positiveDimension(length) > 0
            || positiveDimension(width) > 0
            || positiveDimension(maxDraft) > 0);
    }

    /** Resets boat + dimensions in the search panel (manual reset button). */
    resetSearchFormFields(): void {
        // Must emit: the advanced-filters reload and the collapsed-toolbar summary
        // only learn about the reset through valueChanges.
        this._berthFormGroup.patchValue({
            length: null,
            width: null,
            maxDraft: null,
            boatType: null,
            boat: null,
        });

        this.clearLatestBerthFilterSearchCriteria(true);
    }

    /**
     * After a booking created from the map, drop leftover search values so the
     * next booking is not prefilled with the previous query (boat/person/dimensions).
     */
    clearSearchMaskAfterSuccessfulBooking(): void {
        if (!this.hasActiveSearchCriteria()) {
            return;
        }

        this.resetSearchFormFields();
    }

    /** Whether the newest non-silent map load already used exactly these search-panel values. */
    isLoadedSearch(value: unknown): boolean {
        return deepEqual(value, this._loadedSearch);
    }

    private clearLatestBerthFilterSearchCriteria(clearDimensions: boolean): void {
        if (!this._lastBerthFilters.length) {
            return;
        }

        const latest = this._lastBerthFilters[0];

        this._lastBerthFilters[0] = {
            ...latest,
            boat: undefined,
            ...(clearDimensions
                ? {length: undefined, width: undefined, maxDraft: undefined}
                : {}),
        };
    }

    isTimeSet(date: Date | null | undefined): boolean {
        if (!date) {
            return false; // Kein Datum vorhanden
        }
        return date.getHours() !== 0 || date.getMinutes() !== 0 || date.getSeconds() !== 0 || date.getMilliseconds() !== 0;
    }

    jumpDays(days: number): void {
        if (this.isNowView()) {
            return;
        }

        const {start: currStart, end: currEnd} = this.getDateRange();
        if (!isValidDate(currStart)) {
            return;
        }

        const shift = (date: Date) => days > 0 ? addDays(date, days) : subDays(date, Math.abs(days));
        const startDay = shift(currStart);
        const endDay = isValidDate(currEnd) ? shift(currEnd) : startDay;

        this.setDateRange(this.viewingDayRange(startDay, endDay), false);
        this.updateBerthView();
    }

    /** Verlängert den aktuellen Beginn um so viele Tage. Die Uhrzeit am Beginn bleibt. */
    applyStayLength(days: number): void {
        const current = this.getDateRange();
        const start = isValidDate(current.start) ? new Date(current.start) : this.viewingDayRange(new Date()).start;
        const end = addDays(startOfDay(start), days);
        end.setHours(23, 59, 0, 0);
        this.beginRangeView();
        this.keepSituation = true;
        this.skipNextCentralViewUpdate = true;
        this.setDateRange({start, end}, false);
        this.keepSituation = false;
        this.updateBerthView();
    }

    toggleAdvancedFilters(): void {
        this._advancedBerthFiltersOpen = !this._advancedBerthFiltersOpen;
    }

    public getTimeStringFromSliderValue(value: number): string {

        if (value === 1440) {
            return '23:59';
        }

        const hourStr = this.getHourStringFromSliderValue(value).toString().padStart(2, '0');
        const minuteStr = this.getMinuteStringFromSliderValue(value).toString().padStart(2, '0');

        return `${hourStr}:${minuteStr}`;
    }

    public getHourStringFromSliderValue(value: number): number {
        const hourStr = (value / 60).toString().split('.')[0];
        return Math.min(23, parseInt(hourStr, 10));
    }

    public getMinuteStringFromSliderValue(value: number): number {
        return value === 1440 ? 60 : (value % 60);
    }

    public selectDate(value: DateRangeType): void {
        if (value === DateRangeType.Now) {
            this.showNow();
            return;
        }
        if (value === DateRangeType.Live) {
            this.setLiveMode(true);
            return;
        }

        this.beginRangeView();
        this.currentDateSelectType = value;
        const now = new Date();
        let start: Date;
        let end: Date;

        switch (value) {
            case DateRangeType.RestOfMonth:
                ({start, end} = this.viewingDayRange(now, endOfMonth(now)));
                break;
            case DateRangeType.RestOfWeek:
                ({start, end} = this.viewingDayRange(now, endOfWeek(now)));
                break;
            case DateRangeType.Today:
                this.applySituation('today');
                return;
            case DateRangeType.Tomorrow:
                this.applySituation('tomorrow');
                return;
            default:
                return;
        }

        this.setDateRange({start, end}, false);
        this.updateBerthView();
    }

    updateBerthView(keepBerthFiltersOpen = false, silent = false): void {
        this.updateBerthViewRequest$.next({ keepBerthFiltersOpen, silent });
    }

    private loadBerthViewData(keepBerthFiltersOpen = false, silent = false): void {
        const {length, width, maxDraft, boatType, boat} = this._berthFormGroup.value;
        const {start, end} = this.getDateRange();

        if (!silent) {
            // newest on start!
            this.lastBerthFilters.unshift({
                boat,
                length,
                width,
                maxDraft,
                start,
                end,
                timestamp: Date.now()
            });

            this._lastBerthFilters = this.lastBerthFilters.slice(0, 25);
            this._loadedSearch = {length, width, maxDraft, boatType, boat};
        }

        const filtering = !this.isNowView();
        const httpParams = new HttpParams()
            .set('from', toSqlDateTime(start) || '')
            .set('to', toSqlDateTime(end) || '')
            .set('berthLength', filtering && !isNaN(length) ? String(length) : '')
            .set('berthWidth', filtering && !isNaN(width) ? String(width) : '')
            .set('berthMaxDraft', filtering && !isNaN(maxDraft) ? String(maxDraft) : '')
            .set('berthBoatType', filtering ? (boatType || '') : '');

        this._berthFacade
            .loadBerthMapList(httpParams, { silent })
            .add(() => {
                this._advancedBerthFiltersOpen = this._advancedBerthFiltersOpen && keepBerthFiltersOpen;
            });

        this._electricMeterCabinetsFacade
            .loadAll(getBerthMapElectricMeterCabinetParams())
            .pipe(take(1))
            .subscribe();
    }

    public reset() {
        this.centralDateRangeSubject.next({start: new Date(), end: endOfToday()});
    }

    private initSubscriptions(): void {
        SharedLayerService
            .mapObjectRawList$
            .subscribe(items => {
                if (items?.length) {
                    this.updateBerthView();
                }
            });
    }
}
